import React, { FC, useContext, useEffect, useState } from 'react'
import { compose } from 'redux'
import { connect } from 'react-redux'
import { reset, SubmissionError } from 'redux-form'
import { FormattedMessage, useIntl } from 'react-intl'
import { Grid, Typography } from '@material-ui/core'
import GardenerIcon from '../../../components/icon/GardenerIcon'
import TractorIcon from '../../../components/icon/TractorIcon'
import Button from '../../../components/Button'
import FormButton from '../../../components/form/FormButton'
import BackOfficeContainer, { BO_FILTER_FORM } from '../BackOfficeContainer'
import BoListView from '../components/BoListView'
import NoResultPage from '../../../components/NoResultPage'
import Loader from '../../../components/layout/Loader'
import { DialogContext } from '../../../components/layout/dialog'
import { useSnackbar } from '../../../components/layout/snackbar/SnackbarContext'
import { TYPE_DIALOG } from '../../../utils/constants'
import CompetenceForm from './components/CompetenceForm'
import { createCompetence, getCompetences, updateCompetence } from './services/competence.api'
import { ADD_COMPETENCE_FORM, COMPETENCE_FORM, MESSAGE_BASE } from './services/competence.consts'
import { TYPE_COMPETENCE } from './services/competence.enums'
import { Competence, CompetenceFilters, CompetenceFormValues, CompetenceTuile, IconeTuile } from './services/competence.types'

const INIT_FILTERS: CompetenceFilters = {}

// Icône de la tuile selon le type de compétence
const ICONES_PAR_TYPE: Record<TYPE_COMPETENCE, IconeTuile> = {
	[TYPE_COMPETENCE.MACHINISTE]: TractorIcon,
	[TYPE_COMPETENCE.PARTICIPANT]: GardenerIcon
}

type CompetencesPageProps = {
	resetFilters: (formName: string) => void
}

/**
 * Écran d'administration des compétences
 */
const CompetencesPage: FC<CompetencesPageProps> = ({ resetFilters }) => {
	const intl = useIntl()
	const [filters, setFilters] = useState<CompetenceFilters>(INIT_FILTERS)
	const [competences, setCompetences] = useState<CompetenceTuile[]>([])
	const [selection, setSelection] = useState<CompetenceTuile | undefined>(undefined)
	const [loading, setLoading] = useState<boolean>(false)
	const { snackSuccess, snackError } = useSnackbar()
	const { openDialog, closeDialog } = useContext(DialogContext)

	const formatTuileCompetence = (competence: Competence): CompetenceTuile => ({
		uniqueId: `competence-${competence.id}`,
		icon: ICONES_PAR_TYPE[competence.typeCompetence],
		title: competence.nom,
		data: competence
	})

	const loadCompetences = () => {
		setLoading(true)
		getCompetences(filters)
			.then(competenceList => setCompetences(competenceList.map(formatTuileCompetence)))
			.finally(() => setLoading(false))
	}

	useEffect(() => {
		loadCompetences()
		setSelection(undefined)
	}, [filters])

	const handleCreation = (values: CompetenceFormValues) =>
		createCompetence(values)
			.then(() => {
				snackSuccess({ id: `${MESSAGE_BASE}.snackbar.addCompetence` })
				setSelection(undefined)
				resetFilters(BO_FILTER_FORM)
				setFilters(INIT_FILTERS)
				closeDialog()
			})
			.catch(e => {
				snackError({ id: 'global.errors.form' })
				throw new SubmissionError(e)
			})

	const handleUpdate = (id: number, values: CompetenceFormValues) =>
		updateCompetence({ id, nom: values.nom, description: values.description })
			.then(competence => {
				snackSuccess({ id: `${MESSAGE_BASE}.snackbar.updateCompetence` })
				loadCompetences()
				setSelection(formatTuileCompetence(competence))
			})
			.catch(e => {
				snackError({ id: 'global.errors.form' })
				throw new SubmissionError(e)
			})

	const openPopinCreation = () => {
		openDialog(
			<FormattedMessage id={`${MESSAGE_BASE}.popin.title`} />,
			<Grid item style={{ padding: 20 }}>
				<CompetenceForm
					form={ADD_COMPETENCE_FORM}
					onSubmit={handleCreation}
				/>
			</Grid>,
			[
				<FormButton
					type="primary"
					formName={ADD_COMPETENCE_FORM}
				>
					<FormattedMessage id="actions.create" />
				</FormButton>
			],
			TYPE_DIALOG.ACTION
		)
	}

	return (<>
		<Grid container justify="space-between">
			<Typography variant="h2">
				<FormattedMessage id={`${MESSAGE_BASE}.title`} />
			</Typography>
			<Button type="secondary" keepCase onClick={openPopinCreation}>
				<FormattedMessage id={`${MESSAGE_BASE}.newBtn`} />
			</Button>
		</Grid>
		<BackOfficeContainer inputLabel={`${MESSAGE_BASE}.inputQuery`} onSubmit={setFilters} withFerme={false}>
			{loading ?
				<Loader fromBo />
				: competences.length > 0 ?
					<BoListView
						titleLeft={intl.formatMessage({ id: `${MESSAGE_BASE}.titleLeft` })}
						titleRight={selection?.title}
						tuileList={competences}
						selection={selection}
						handleSelection={setSelection}
						formName={COMPETENCE_FORM}
					>
						{selection && <CompetenceForm
							form={COMPETENCE_FORM}
							onSubmit={(values: CompetenceFormValues) => handleUpdate(selection.data.id, values)}
							isEdition
							initialValues={{
								nom: selection.data.nom,
								typeCompetence: selection.data.typeCompetence,
								description: selection.data.description
							}}
						/>}
					</BoListView>
					: <NoResultPage />
			}
		</BackOfficeContainer>
	</>)
}

const actions = {
	resetFilters: reset
}

export default compose<FC>(
	connect(undefined, actions)
)(CompetencesPage)
